import { supabase } from '../lib/supabase';

export const projectService = {
  async getAll(userId) {
    const { data, error } = await supabase?.from('projects')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    // Convert snake_case to camelCase
    return data?.map(row => ({
      id: row?.id,
      userId: row?.user_id,
      name: row?.name,
      prompt: row?.prompt,
      thumbnail: row?.thumbnail,
      thumbnailAlt: row?.thumbnail_alt,
      techStack: row?.tech_stack || [],
      fileCount: row?.file_count,
      projectSize: row?.project_size,
      generationDuration: row?.generation_duration,
      zipUrl: row?.zip_url,
      isNew: row?.is_new,
      createdAt: row?.created_at,
      lastModified: row?.last_modified
    })) || [];
  },

  async getById(projectId, userId) {
    const { data, error } = await supabase?.from('projects')?.select('*')?.eq('id', projectId)?.eq('user_id', userId)?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      userId: data?.user_id,
      name: data?.name,
      prompt: data?.prompt,
      thumbnail: data?.thumbnail,
      thumbnailAlt: data?.thumbnail_alt,
      techStack: data?.tech_stack || [],
      fileCount: data?.file_count,
      projectSize: data?.project_size,
      generationDuration: data?.generation_duration,
      zipUrl: data?.zip_url,
      isNew: data?.is_new,
      createdAt: data?.created_at,
      lastModified: data?.last_modified
    };
  },

  async create(project) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const { data, error } = await supabase?.from('projects')?.insert({
        user_id: user?.id,
        name: project?.name,
        prompt: project?.prompt,
        thumbnail: project?.thumbnail,
        thumbnail_alt: project?.thumbnailAlt,
        tech_stack: project?.techStack,
        file_count: project?.fileCount,
        project_size: project?.projectSize,
        generation_duration: project?.generationDuration,
        zip_url: project?.zipUrl,
        is_new: true
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      userId: data?.user_id,
      name: data?.name,
      prompt: data?.prompt,
      thumbnail: data?.thumbnail,
      thumbnailAlt: data?.thumbnail_alt,
      techStack: data?.tech_stack || [],
      fileCount: data?.file_count,
      projectSize: data?.project_size,
      generationDuration: data?.generation_duration,
      zipUrl: data?.zip_url,
      isNew: data?.is_new,
      createdAt: data?.created_at,
      lastModified: data?.last_modified
    };
  },

  async update(projectId, updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const dbUpdates = {};
    if (updates?.name !== undefined) dbUpdates.name = updates?.name;
    if (updates?.prompt !== undefined) dbUpdates.prompt = updates?.prompt;
    if (updates?.thumbnail !== undefined) dbUpdates.thumbnail = updates?.thumbnail;
    if (updates?.thumbnailAlt !== undefined) dbUpdates.thumbnail_alt = updates?.thumbnailAlt;
    if (updates?.techStack !== undefined) dbUpdates.tech_stack = updates?.techStack;
    if (updates?.fileCount !== undefined) dbUpdates.file_count = updates?.fileCount;
    if (updates?.projectSize !== undefined) dbUpdates.project_size = updates?.projectSize;
    if (updates?.isNew !== undefined) dbUpdates.is_new = updates?.isNew;
    
    const { data, error } = await supabase?.from('projects')?.update(dbUpdates)?.eq('id', projectId)?.eq('user_id', user?.id)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      userId: data?.user_id,
      name: data?.name,
      prompt: data?.prompt,
      thumbnail: data?.thumbnail,
      thumbnailAlt: data?.thumbnail_alt,
      techStack: data?.tech_stack || [],
      fileCount: data?.file_count,
      projectSize: data?.project_size,
      generationDuration: data?.generation_duration,
      zipUrl: data?.zip_url,
      isNew: data?.is_new,
      createdAt: data?.created_at,
      lastModified: data?.last_modified
    };
  },

  async delete(projectId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const { error } = await supabase?.from('projects')?.delete()?.eq('id', projectId)?.eq('user_id', user?.id);
    
    if (error) throw error;
  },

  async bulkDelete(projectIds) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const { error } = await supabase?.from('projects')?.delete()?.in('id', projectIds)?.eq('user_id', user?.id);
    
    if (error) throw error;
  }
};

export default projectService;